import { reactive, readonly } from 'vue';
import type { AxiosRequestConfig, AxiosResponse } from 'axios';
import type {
  CampoDiagnostico,
  DiagnosticoTela,
  EventoDiagnostico,
  OperacaoDiagnostico,
} from '@/models/assistente/sofia.model';

// Diagnóstico voluntário, transitório e independente do perfil. Nunca guarda os dados brutos.
const estado = reactive({
  acompanhando: false,
  operacoes: [] as OperacaoDiagnostico[],
  eventos: [] as EventoDiagnostico[],
});
const SENSIVEL =
  /senha|password|secret|token|auth|cookie|cpf|cnpj|email|e-mail|telefone|celular|nome|name|endereco|address|conexao|connection|login|usuario|user|documento|rg$|nascimento|birth|filiacao|mae|pai|cep|ip$|arquivo|file|foto|photo|base64|observac|descricao|mensagem|message|texto|text|url|uri|trace|stack/i;
const ROTAS_SEGURAS = new Set(
  'api v1 v2 aluno alunos turma turmas matricula matriculas enturmacao avaliacao notas faltas ensino serie grade disciplina conteudo distribuicao configuracao configuracoes parametros gesparametros instituicao escola escolas lista listar obter incluir atualizar excluir salvar pesquisar retornaralunosemcurso fechamentoturmas calendario professor pessoal merenda biblioteca transporte patrimonio prestacaocontas ged centralvagas prematricula bibemprestimo relatorios lancamentonotas aula conteudolecionado users listaturmasdisciplinas listaturmas listaalunos listanotas listafaltas getall getbyid getlista configuracao configurar pesquisaraluno estrutura etapas calculo calcular calculonotas'.split(
    ' ',
  ),
);
const ESTADOS_SEGUROS = new Set(
  'OK ERROR ERRO SUCESSO SUCCESS TRUE FALSE EM_CURSO APROVADO REPROVADO INSCRITA DESIGNADA MATRICULADA CANCELADA ATIVO INATIVO PENDENTE CONCLUIDO BLOQUEADO LIBERADO GET POST PUT PATCH DELETE HEAD OPTIONS NETWORK_ERROR ERR_NETWORK ECONNABORTED ERR_CANCELED'.split(
    ' ',
  ),
);
const CHAVES_SEGURAS = new Set(
  'id codigo ano etapa semestre status situacao tipo sucesso success valido valid ativo active bloqueado readonly disabled quantidade total valor nota pontos peso media faltas limite data datainicio datafim datainclusao dataalteracao datalimite resultado result dados data itens items lista list erros errors codigoescola codigoturma ensinoid escolaid turmaid matriculaid avaliacaoid conteudoid serieid podeeditar podeincluir podealterar podeexcluir emcurso aprovado reprovado temnota temfrequencia possuideficiencia redutoraee totalcount count rows dataentrada datasaida datainicioetapa datafimetapa permiteavaliacao permitetotaletapa turmafechada podegravarnotas podegravarfaltas enturmacaoefetiva enturmacaonaoefetiva valoravaliacao valormaximo maxnota acima maximo formula formuladistribuicao temformula codigomatricula codigoenturmacao codigoconteudo codigodisciplina codigoavaliacao codigoensino avaliacoes enturmacoes matriculas notas etapas'.split(
    ' ',
  ),
);
// Nomes estáticos de campos pessoais são úteis para localizar uma pendência, mas seus valores
// nunca são retornados. Senhas e segredos nem participam da inspeção.
const CHAVES_CAMPOS = new Set([
  ...CHAVES_SEGURAS,
  ...'nome nomecompleto nomealuno nomeestudante nomeservidor nomeusuario email telefone celular cpf cnpj login usuario endereco bairro municipio cep datanascimento nascimento filiacao1 filiacao2'.split(
    ' ',
  ),
]);
const ACOES_SEGURAS = new Map([
  ['salvar', 'salvar'],
  ['gravar', 'gravar'],
  ['cadastrar', 'cadastrar'],
  ['incluir', 'incluir'],
  ['excluir', 'excluir'],
  ['matricular', 'matricular'],
  ['confirmar', 'confirmar'],
  ['enviar', 'enviar'],
  ['salvar notas', 'salvarnotas'],
  ['gravar notas', 'gravarnotas'],
  ['salvar alterações', 'salvaralteracoes'],
  ['salvar conteúdo', 'salvarconteudo'],
]);
const CLASSES_ERRO = new Set([
  'Error',
  'TypeError',
  'SyntaxError',
  'ReferenceError',
  'RangeError',
  'URIError',
  'EvalError',
  'AggregateError',
  'AxiosError',
  'CanceledError',
  'DOMException',
]);

function textoSeguro(valor: string): string {
  if (
    /^\d{4}-\d{2}-\d{2}(?:T\d{2}:\d{2}:\d{2}(?:\.\d{1,7})?(?:Z|[+-]\d{2}:\d{2})?)?$/.test(valor)
  ) {
    return valor;
  }
  // Só códigos/estados curtos: não tenta "limpar" mensagens livres que podem conter pessoas.
  if (ESTADOS_SEGUROS.has(valor)) return valor;
  return '[texto omitido]';
}

function mensagemSegura(valor: string): string {
  const categorias: [RegExp, string][] = [
    [/Cannot read (properties|property).*?(null|undefined)/i, 'ERRO_PROPRIEDADE_NULA'],
    [/is not a function/i, 'ERRO_FUNCAO_INEXISTENTE'],
    [/is not defined/i, 'ERRO_REFERENCIA_INDEFINIDA'],
    [/Failed to fetch|Network Error/i, 'ERRO_REDE'],
    [/JSON.*position|Unexpected token.*JSON/i, 'ERRO_JSON_INVALIDO'],
  ];
  return categorias.find(([regra]) => regra.test(valor))?.[1] ?? textoSeguro(valor);
}

function sanitizar(valor: unknown, nivel = 0): unknown {
  if (valor === null || typeof valor === 'boolean') return valor;
  if (typeof valor === 'number') return Number.isFinite(valor) ? valor : null;
  if (typeof valor === 'string') return textoSeguro(valor);
  if (nivel >= 4) return '[estrutura omitida]';
  if (Array.isArray(valor)) return valor.slice(0, 8).map((v) => sanitizar(v, nivel + 1));
  if (!valor || typeof valor !== 'object') return '[conteúdo omitido]';
  const prototipo = Object.getPrototypeOf(valor);
  if (prototipo !== Object.prototype && prototipo !== null) return '[conteúdo omitido]';
  const resultado: Record<string, unknown> = {};
  let indice = 0;
  for (const chave of Object.keys(valor).slice(0, 20)) {
    if (SENSIVEL.test(chave) || ['__proto__', 'constructor', 'prototype'].includes(chave)) continue;
    const descritor = Object.getOwnPropertyDescriptor(valor, chave);
    // Não executa getters: telemetria não pode provocar efeitos na aplicação.
    if (!descritor || !('value' in descritor)) continue;
    const destino = CHAVES_SEGURAS.has(chave.toLowerCase()) ? chave : `campo${++indice}`;
    resultado[destino] = sanitizar(descritor.value, nivel + 1);
  }
  return resultado;
}

function corpoSeguro(valor: unknown): unknown {
  if (valor === undefined) return undefined;
  // Axios pode fornecer JSON já serializado, mas nunca analisa arquivos/formulários/texto livre.
  if (typeof valor === 'string') {
    if (valor.length > 50000) return { resumo: 'estrutura excede limite' };
    try {
      valor = JSON.parse(valor);
    } catch {
      return '[texto omitido]';
    }
  }
  const limpo = sanitizar(valor);
  return JSON.stringify(limpo).length <= 1200 ? limpo : { resumo: 'estrutura excede limite' };
}

function caminhoSeguro(config: AxiosRequestConfig): string | null {
  const referencia =
    typeof window !== 'undefined' ? window.location.origin : 'https://local.invalid';
  const base = new URL(config.baseURL || '/', referencia);
  const requisicao = config.url || '';
  const absoluta = /^(?:https?:)?\/\//i.test(requisicao)
    ? requisicao
    : `${base.href.replace(/\/+$/, '')}/${requisicao.replace(/^\/+/, '')}`;
  const url = new URL(absoluta, referencia);
  const pertence = [process.env.VUE_APP_ACADEMICO_API, process.env.VUE_APP_KERNEL_API].some(
    (api) => {
      if (!api) return false;
      const permitida = new URL(api, referencia);
      const prefixo = permitida.pathname.replace(/\/+$/, '');
      return (
        url.origin === permitida.origin &&
        (url.pathname === prefixo || url.pathname.startsWith(`${prefixo}/`))
      );
    },
  );
  if (!pertence) return null;
  const partes = url.pathname
    .split('/')
    .filter(Boolean)
    .map((p) => decodeURIComponent(p));
  if (
    partes.some((p) =>
      /auth|login|token|renovar|refresh|autenticacao|logout|signout|signin|sair/i.test(p),
    )
  )
    return null;
  return (
    '/' +
    partes
      .slice(0, 12)
      .map((parte) => {
        if (/^-?\d+$/.test(parte)) return ':id';
        return ROTAS_SEGURAS.has(parte.toLowerCase()) ? parte.toLowerCase() : ':segmento';
      })
      .join('/')
  );
}

function camposAtuais(): CampoDiagnostico[] {
  if (typeof document === 'undefined') return [];
  const campos: CampoDiagnostico[] = [];
  for (const elemento of document.querySelectorAll('input,select,textarea,button')) {
    if (elemento.closest('.painel-sofia') || !elemento.getClientRects().length) continue;
    if (elemento.tagName === 'BUTTON') {
      // Remove somente os ícones antes da comparação exata. Nunca retorna o texto do botão.
      const copia = elemento.cloneNode(true) as HTMLElement;
      copia
        .querySelectorAll('i,svg,.mdi,.fa,[aria-hidden="true"]')
        .forEach((icone) => icone.remove());
      const rotulo = (elemento.getAttribute('aria-label') || copia.textContent || '')
        .normalize('NFC')
        .replace(/\s+/g, ' ')
        .trim()
        .toLowerCase();
      const acao = ACOES_SEGURAS.get(rotulo);
      if (!acao) continue;
      campos.push({
        campo: acao,
        tipo: 'button',
        bloqueado:
          elemento.matches(':disabled') || elemento.getAttribute('aria-disabled') === 'true',
        invalido: false,
        preenchido: false,
      });
      continue;
    }
    const campo = elemento as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
    const tipo = campo.type;
    const identificador = campo.id || campo.name;
    if (
      tipo === 'password' ||
      tipo === 'hidden' ||
      /password|senha|token|secret|auth|cookie/i.test(identificador)
    )
      continue;
    // Identificadores livres também podem incluir dados. Só devolve os conhecidos, sem valores.
    const canonico = identificador
      .toLowerCase()
      .replace(/^(txt|input|campo|ddl|select|chk)/, '')
      .replace(/[_-]?\d+$/, '');
    const nome = CHAVES_CAMPOS.has(canonico) ? canonico : `campo${campos.length + 1}`;
    const marcado = tipo === 'checkbox' || tipo === 'radio';
    campos.push({
      campo: nome,
      tipo,
      bloqueado:
        campo.matches(':disabled') ||
        ('readOnly' in campo && campo.readOnly) ||
        campo.getAttribute('aria-disabled') === 'true',
      invalido: campo.getAttribute('aria-invalid') === 'true' || !campo.validity.valid,
      preenchido: marcado ? (campo as HTMLInputElement).checked : campo.value.length > 0,
    });
  }
  return campos
    .sort(
      (a, b) =>
        Number(b.tipo === 'button' && b.bloqueado) - Number(a.tipo === 'button' && a.bloqueado) ||
        Number(b.invalido) - Number(a.invalido) ||
        Number(b.bloqueado) - Number(a.bloqueado),
    )
    .slice(0, 30);
}

class DiagnosticoTelaService {
  readonly estado = readonly(estado);
  private inicios = new WeakMap<object, number>();
  private prazo: ReturnType<typeof setTimeout> | undefined;
  private consoleError: typeof console.error | undefined;
  private consoleWarn: typeof console.warn | undefined;
  private wrapperError: typeof console.error | undefined;
  private wrapperWarn: typeof console.warn | undefined;
  private erroJanela = (evento: ErrorEvent) =>
    this.registrarEvento('window.error', evento.error ?? evento.message);
  private rejeicao = (evento: PromiseRejectionEvent) =>
    this.registrarEvento('unhandledrejection', evento.reason);
  private sairPagina = () => this.parar();

  iniciar(): void {
    if (estado.acompanhando) return;
    this.limpar();
    estado.acompanhando = true;
    this.consoleError = console.error;
    this.consoleWarn = console.warn;
    const errorOriginal = this.consoleError;
    const warnOriginal = this.consoleWarn;
    this.wrapperError = (...args: unknown[]) => {
      this.registrarEvento('console.error', args[0]);
      errorOriginal.apply(console, args);
    };
    this.wrapperWarn = (...args: unknown[]) => {
      this.registrarEvento('console.warn', args[0]);
      warnOriginal.apply(console, args);
    };
    console.error = this.wrapperError;
    console.warn = this.wrapperWarn;
    if (typeof window !== 'undefined') {
      window.addEventListener('error', this.erroJanela);
      window.addEventListener('unhandledrejection', this.rejeicao);
      window.addEventListener('pagehide', this.sairPagina);
    }
    this.prazo = setTimeout(() => this.parar(), 15 * 60 * 1000);
  }

  parar(): void {
    estado.acompanhando = false;
    if (console.error === this.wrapperError && this.consoleError) console.error = this.consoleError;
    if (console.warn === this.wrapperWarn && this.consoleWarn) console.warn = this.consoleWarn;
    this.wrapperError = this.wrapperWarn = undefined;
    this.consoleError = this.consoleWarn = undefined;
    if (typeof window !== 'undefined') {
      window.removeEventListener('error', this.erroJanela);
      window.removeEventListener('unhandledrejection', this.rejeicao);
      window.removeEventListener('pagehide', this.sairPagina);
    }
    clearTimeout(this.prazo);
    this.prazo = undefined;
    this.limpar();
  }

  limpar(): void {
    estado.operacoes.splice(0);
    estado.eventos.splice(0);
    this.inicios = new WeakMap<object, number>();
  }

  listar(): DiagnosticoTela {
    return {
      acompanhando: estado.acompanhando,
      operacoes: JSON.parse(JSON.stringify(estado.operacoes)),
      eventos: estado.eventos.map((e) => ({ ...e })),
      campos: estado.acompanhando ? camposAtuais() : [],
    };
  }

  registrarInicioAxios(config: AxiosRequestConfig): void {
    try {
      if (!estado.acompanhando) return;
      if (/logout|signout|sair/i.test(config.url || '')) {
        this.parar();
        return;
      }
      if (caminhoSeguro(config)) this.inicios.set(config, Date.now());
    } catch {
      /* O diagnóstico nunca interrompe a requisição. */
    }
  }

  registrarRespostaAxios(resposta: AxiosResponse): void {
    this.registrarOperacao(resposta.config, resposta.status, resposta.data);
  }

  registrarErroAxios(erro: unknown): void {
    try {
      if (!estado.acompanhando || !erro || typeof erro !== 'object') return;
      const axios = erro as { config?: AxiosRequestConfig; response?: AxiosResponse };
      if (axios.config)
        this.registrarOperacao(axios.config, axios.response?.status ?? 0, axios.response?.data);
    } catch {
      /* O diagnóstico nunca interfere no tratamento do erro. */
    }
  }

  private registrarOperacao(config: AxiosRequestConfig, status: number, retorno: unknown): void {
    try {
      if (!estado.acompanhando) return;
      const url = caminhoSeguro(config);
      if (!url) return;
      const inicio = this.inicios.get(config);
      this.inicios.delete(config);
      // O WeakMap é renovado a cada limpeza/início: respostas de outra captura não entram.
      // Também não aceita requisição disparada antes de a pessoa começar a acompanhar.
      if (inicio === undefined) return;
      const verbo = (config.method || 'GET').toUpperCase();
      estado.operacoes.push({
        quando: new Date().toISOString(),
        metodo: ['GET', 'POST', 'PUT', 'PATCH', 'DELETE', 'HEAD', 'OPTIONS'].includes(verbo)
          ? verbo
          : 'OUTRO',
        url,
        status: Number.isFinite(status) ? status : 0,
        duracaoMs: Math.max(0, Date.now() - inicio),
        envio: corpoSeguro(config.data),
        retorno: corpoSeguro(retorno),
      });
      if (estado.operacoes.length > 4) {
        // Reservar até duas posições para escritas/erros evita que GETs de recarga apaguem
        // imediatamente a operação que a pessoa acabou de executar.
        const prioridade = estado.operacoes
          .filter(
            (o) =>
              o.status === 0 || o.status >= 400 || !['GET', 'HEAD', 'OPTIONS'].includes(o.metodo),
          )
          .slice(-2);
        const restantes = estado.operacoes
          .filter((o) => !prioridade.includes(o))
          .slice(-(4 - prioridade.length));
        const selecionadas = estado.operacoes.filter(
          (o) => prioridade.includes(o) || restantes.includes(o),
        );
        estado.operacoes.splice(0, estado.operacoes.length, ...selecionadas);
      }
    } catch {
      /* Sem dados brutos nem exceções do coletor na aplicação. */
    }
  }

  private registrarEvento(tipo: string, valor: unknown): void {
    try {
      if (!estado.acompanhando) return;
      const classe = valor instanceof Error && CLASSES_ERRO.has(valor.name) ? valor.name : 'Error';
      const texto =
        typeof valor === 'string'
          ? mensagemSegura(valor)
          : valor instanceof Error
            ? mensagemSegura(valor.message)
            : '[texto omitido]';
      estado.eventos.push({
        quando: new Date().toISOString(),
        tipo,
        mensagem: `${classe}: ${texto === '[texto omitido]' ? '[mensagem omitida]' : texto}`,
      });
      if (estado.eventos.length > 8) estado.eventos.splice(0, estado.eventos.length - 8);
    } catch {
      /* Não inspeciona stack, arquivo, argumentos adicionais ou objetos arbitrários. */
    }
  }
}

export default new DiagnosticoTelaService();
